import { useState, useCallback } from 'react';

const unitImages = [
  { type: '59A', url: 'https://storage.helloreaddy.io/project_files/e87dc29c-feba-40d3-9c10-bae38306932e/f53ec64f-bedb-4955-a73a-082ba8174cf0_compressed_59A.webp' },
  { type: '59B', url: 'https://storage.helloreaddy.io/project_files/e87dc29c-feba-40d3-9c10-bae38306932e/1dc6771a-2446-42b2-a738-1acfb57528aa_compressed_59B.webp' },
  { type: '59C', url: 'https://storage.helloreaddy.io/project_files/e87dc29c-feba-40d3-9c10-bae38306932e/bc8c7c09-5904-496a-820b-81937bbc85fb_compressed_59C.webp' },
  { type: '59D', url: 'https://storage.helloreaddy.io/project_files/e87dc29c-feba-40d3-9c10-bae38306932e/f69684a6-3674-45bd-bd90-95f2d6596811_compressed_59D.webp' },
  { type: '74A', url: 'https://storage.helloreaddy.io/project_files/e87dc29c-feba-40d3-9c10-bae38306932e/0f789aa5-0ba9-4a33-8e48-83d3c438898f_compressed_74a.webp' },
  { type: '74B', url: 'https://storage.helloreaddy.io/project_files/e87dc29c-feba-40d3-9c10-bae38306932e/b1294b66-e949-4fc9-8176-f746826c3f3b_compressed_74B.webp' },
  { type: '74C', url: 'https://storage.helloreaddy.io/project_files/e87dc29c-feba-40d3-9c10-bae38306932e/08700bfe-8a45-4088-89bb-994a27da85cc_compressed_74C.webp' },
];

export default function UnitSection() {
  const [lightbox, setLightbox] = useState<{ open: boolean; index: number }>({ open: false, index: 0 });

  const openLightbox = useCallback((index: number) => {
    setLightbox({ open: true, index });
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox({ open: false, index: 0 });
  }, []);

  const nextImage = useCallback(() => {
    setLightbox((prev) => ({ ...prev, index: (prev.index + 1) % unitImages.length }));
  }, []);

  const prevImage = useCallback(() => {
    setLightbox((prev) => ({ ...prev, index: (prev.index - 1 + unitImages.length) % unitImages.length }));
  }, []);

  return (
    <section id="unit" className="relative w-full overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://www.xn--2w2b1dm8gn5nwya81ag35e.com/assets/images/main/unit-bg.jpg"
          alt="Unit background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-50/95 via-background-50/80 to-background-50/60" />
      </div>

      <div className="relative z-10 section-padding py-20 md:py-28">
        <div className="w-full">
          {/* Title */}
          <div className="mb-10 md:mb-14 text-center">
            <p className="text-xs md:text-sm font-medium text-primary-500 tracking-[0.3em] mb-2">
              HEYRI AUVERSONE
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-50 mb-3">
              UNIT PLAN
            </h2>
            <p className="text-sm md:text-base text-foreground-300">
              헤이리 오베르원 <span className="text-primary-500 font-semibold">평형안내</span>
            </p>
          </div>

          {/* Floor Plan Gallery */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-5xl mx-auto">
            {unitImages.map((unit, index) => (
              <button
                key={unit.type}
                type="button"
                onClick={() => openLightbox(index)}
                className="group relative overflow-hidden rounded-xl border border-background-200/40 bg-background-50/60 backdrop-blur-sm hover:border-primary-500/30 transition-all duration-500 text-left cursor-pointer"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={unit.url}
                    alt={`${unit.type} 평면도`}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 px-4 py-3 bg-gradient-to-t from-black/60 to-transparent">
                  <span className="text-sm font-bold text-background-50">
                    {unit.type} 타입
                  </span>
                </div>
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-primary-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-background-50/90 flex items-center justify-center">
                    <i className="ri-zoom-in-line text-lg text-foreground-50" />
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Unit Types */}
          <div className="mt-10 md:mt-14 flex flex-wrap justify-center gap-3">
            {['59A', '59B', '59C', '59D', '74A', '74B', '74C'].map((type) => (
              <div
                key={type}
                className="px-5 py-2.5 bg-background-100/50 border border-background-200/30 rounded-full"
              >
                <span className="text-sm font-semibold text-foreground-200">{type}</span>
                <span className="text-xs text-foreground-500 ml-1">㎡</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox.open && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 md:p-8"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
        >
          {/* Close button */}
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 md:top-6 md:right-6 w-10 h-10 rounded-full bg-background-50/20 backdrop-blur-sm flex items-center justify-center hover:bg-background-50/40 transition-colors z-10"
          >
            <i className="ri-close-line text-xl text-background-50" />
          </button>

          {/* Prev button */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50/20 backdrop-blur-sm flex items-center justify-center hover:bg-background-50/40 transition-colors z-10"
          >
            <i className="ri-arrow-left-s-line text-xl text-background-50" />
          </button>

          {/* Next button */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50/20 backdrop-blur-sm flex items-center justify-center hover:bg-background-50/40 transition-colors z-10"
          >
            <i className="ri-arrow-right-s-line text-xl text-background-50" />
          </button>

          {/* Image */}
          <div
            className="max-w-5xl w-full max-h-[85vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={unitImages[lightbox.index].url}
              alt={`${unitImages[lightbox.index].type} 평면도`}
              className="max-w-full max-h-[78vh] object-contain rounded-lg"
            />
            <p className="mt-3 text-sm font-medium text-background-50">
              {unitImages[lightbox.index].type} 타입 — {lightbox.index + 1} / {unitImages.length}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}